import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../../components/common/PageHeader/PageHeader";
import DownloadLabel from "../../../components/common/DownloadLabel/Download";
import ExportLabel from "../../../components/common/ExportLabel/Export";
import AddButton from "../../../components/common/AddButton/AddButton";
import Filter from "../../../components/common/Filter/Filter";
import EditIcon from "../../../components/Table/EditIcon/EditIcon";
import ArchiveIcon from "../../../components/Table/ArchiveIcon/ArchiveIcon";
import ArchivePopup from "../../../components/popup/ArchivePopup/ArchivePopup";
import {
  ArchiveAssignedRoute,
  getRouteAssign,
} from "../../../services/routeApi";
import Sort from "../../../components/common/Sort/Sort";
import SimpleSearchBox from "../../../components/common/Search/SimpleSearch";
import { useCheckboxClick } from "../../../checkboxclick";
import { baseURL } from "../../../services/api";
import ArchiveSelectedItem from "../../../components/common/ArchiveSelectedItemLabel/ArchiveSelected";
import NewButton from "../../../components/common/AddButton/NewButton";
import SelectedItemsCount from "../../../components/common/TotalCount/SelectedItemsCount";
import { formatDate } from "../../../utils/dates";
import EmptyState from "../../../components/common/EmptyState/EmptyState";
import NoRoute from "../../../assets/empty-states/Route/NoRoute.svg";
import DeleteIcon from "../../../components/Table/DeleteIcon/DeleteIcon";
import DeletePopup from "../../../components/popup/DeletePopup/DeletePopUp";
import { deleteAlert } from "../../../components/common/ErrorAlert/DeleteAlert";
import { DeleteRouteAssign } from "../../../services/routeApi";
import { useTranslation } from "react-i18next";
import { showAlert } from "../../../components/common/ErrorAlert/ErrorAlert";

const RouteAssignList = () => {
  const { t } = useTranslation();
  const [routeAssignments, setRouteAssignments] = useState([]);
  const [searchParams, setSearchParams] = useState({ term: "" });
  const [selectedSortOption, setSelectedSortOption] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedFilterOption, setSelectedFilterOption] = useState("Default");
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const filterOptions = ["All", "Archived", "Unarchived"];
  const [isArchivePopupOpen, setIsArchivePopupOpen] = useState(false);
  const {
    isParentSelected,
    selectedItems,
    handleParentCheckboxChange,
    handleChildCheckboxChange,
    resetCheckboxState,
  } = useCheckboxClick(routeAssignments);
  const sortOptions = ["A to Z", "Z to A", "Oldest", "Newest"];
  const [meta, setMeta] = useState({});
  const navigate = useNavigate();
  const [exportState, setExportState] = useState({
    isExporting: false,
    error: null,
  });
  const mapSortOptionToQuery = (option) => {
    switch (option) {
      case "A to Z":
        return "a_to_z";
      case "Z to A":
        return "z_to_a";
      case "Newest":
        return "newest";
      case "Oldest":
        return "oldest";
      default:
        return "newest";
    }
  };
  const params = {
    search: searchParams.term,
    search_argument: searchParams.argument,
    filter:
      selectedFilterOption === "All"
        ? undefined
        : selectedFilterOption.toLowerCase(),
    sort: mapSortOptionToQuery(selectedSortOption),
  };

  const handleDelete = async () => {
    try {
      if (!selectedItem?.id) return;

      const response = await DeleteRouteAssign(selectedItem.id);

      if (response?.data) {
        setIsDeletePopupOpen(false);
        fetchRouteAssignments();
        resetCheckboxState();

        deleteAlert(t("Route assignment deleted successfully!"), "success");
      }
    } catch (error) {
      if (error?.response?.data?.error_code === 1001) {
        showAlert(error?.response?.data?.message);
      }
    }
  };

  useEffect(() => {
    fetchRouteAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, selectedFilterOption, selectedSortOption]);

  const fetchRouteAssignments = async () => {
    try {
      const response = await getRouteAssign(null, params);

      if (response?.data) {
        setRouteAssignments(response.data.data || []);
        setMeta(response.data.meta || {}); // ⭐ ADD THIS
      } else {
        setRouteAssignments([]);
        setMeta({ has_data: false });
      }
    } catch (e) {
      if (e?.response?.data?.error_code === 1001) navigate("/error");
    }
  };

  async function HandleArchive() {
    await ArchiveAssignedRoute(selectedItem.id, null);
    closePopups();
    fetchRouteAssignments();
  }

  const handleArchiveSelectedItems = async () => {
    try {
      await ArchiveAssignedRoute(null, selectedItems);

      fetchRouteAssignments();
      resetCheckboxState();
    } catch (error) {
      console.error("Error archiving/unarchiving selected items:", error);
    }
  };

  const handleSearchSelect = (term) => {
    setSearchParams({ term });
  };

  const handleFilterSelect = (option) => {
    setSelectedFilterOption(option);
  };

  const handleSortOptionSelect = (option) => {
    setSelectedSortOption(option);
  };
  useEffect(() => {
    console.log(selectedSortOption);
  }, [selectedSortOption]);

  const handleEditButtonClick = (id) => {
    navigate(`/routes/assign/edit/${id}`);
  };

  const handleAddButtonClick = () => {
    navigate("/routes/assign/add");
  };

  const handleArchiveClick = (item) => {
    setSelectedItem(item);
    setIsArchivePopupOpen(true);
  };

  const handleExportStart = () => {
    setExportState({ isExporting: true, error: null });
  };

  const handleExportComplete = () => {
    setExportState({ isExporting: false, error: null });
    resetCheckboxState();
  };

  const handleExportError = (error) => {
    setExportState({ isExporting: false, error });
  };

  const closePopups = () => {
    setIsArchivePopupOpen(false);
  };

  const handleDetailsClick = (id) => {
    navigate(`/routes/assign/details/${id}`);
  };

  return (
    <div className="whole-container">
      <div className="path-header">
        <PageHeader
          pageName="Route Management / "
          nestedpageName="Route Assignment"
        />

        <div className="path-header-buttons">
          <ArchiveSelectedItem
            selectedItems={selectedItems}
            items={routeAssignments}
            onArchiveSelected={handleArchiveSelectedItems}
          />

          <ExportLabel
            exportUrl={`${baseURL}route/route-assign/export/`}
            selectedIds={selectedItems}
            onExportStart={handleExportStart}
            onExportComplete={handleExportComplete}
            onExportError={handleExportError}
          />
        </div>
      </div>

      <div className="pagename-container">
        <div className="sub-page-left ">
          <div className="name-utility">
            <span className="page-name">
              {t("Route Assignment")}
            </span>
          </div>
          <SimpleSearchBox
            placeholder="Search"
            onSearchSelect={handleSearchSelect}
          />
          <SelectedItemsCount count={selectedItems.length} />
        </div>
        <div className="sub-head-r-side-icons">
          <NewButton onClick={handleAddButtonClick} />

          <div className="group-by-box">
            <Filter
              options={filterOptions}
              onFilterSelect={handleFilterSelect}
              defaultValue={selectedFilterOption}
            />
          </div>
          <div className="group-by-box">
            <Sort
              options={sortOptions}
              onOptionSelect={handleSortOptionSelect}
            />
          </div>
        </div>
      </div>
      <div
        className={`table-container ${routeAssignments.length === 0 ? "no-hover-table" : ""}`}
      >
        <table className="common-table">
          <thead className="common-table-head">
            <tr className="common-table-thead-tr">
              <th>
                <input
                  className="table-selectbox table-parent-selectbox"
                  type="checkbox"
                  checked={isParentSelected}
                  onChange={handleParentCheckboxChange}
                />
                {t("Name")}
              </th>
              <th>{t("Sales Person")}</th>
              <th>{t("Start Date")}</th>
              <th>{t("End Date")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="common-table-tbody">
            {routeAssignments.length > 0 ? (
              routeAssignments.map((assignment) => (
                <tr
                  key={assignment.id}
                  className={assignment.is_archived ? "archived-item" : ""}

                  onClick={() => handleDetailsClick(assignment.id)}>
                  <td>
                    <input
                      className="table-selectbox table-child-selectbox"
                      type="checkbox"
                      checked={selectedItems.includes(assignment.id)}
                      onChange={() => handleChildCheckboxChange(assignment.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {assignment.route?.name}
                  </td>
                  <td>{assignment.sales_person?.name}</td>
                  <td>{formatDate(assignment.start_date)}</td>
                  <td>{formatDate(assignment.end_date)}</td>
                  <td>
                    <div className="actions common-action">
                      <EditIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditButtonClick(assignment.id);
                        }}
                      />
                      <ArchiveIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArchiveClick(assignment);
                        }}
                        isArchived={assignment.is_archived}
                      />
                      <DeleteIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedItem(assignment);
                          setIsDeletePopupOpen(true);
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" style={{ padding: 0, border: "none" }}>
                  <EmptyState
                    hasData={meta?.has_data}
                    image={NoRoute}
                    heading={t("No Route Assignments Yet")}
                    subText={t(
                      "Assigned routes will appear here once created.",
                    )}
                    buttonText="+ Assign Route"
                    onButtonClick={handleAddButtonClick}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <ArchivePopup
        isOpen={isArchivePopupOpen}
        // archivename={selectedItem?.route?.name || ''}
        archivename={"this record"}
        isArchived={selectedItem?.is_archived || false}
        onSave={HandleArchive}
        onClose={closePopups}
      />
      <DeletePopup
        isOpen={isDeletePopupOpen}
        onSave={handleDelete}
        onClose={() => setIsDeletePopupOpen(false)}
      />
    </div>
  );
};

export default RouteAssignList;
